import {
  Badge,
  Button,
  CardProps,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
  Text,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { Address, FreightService } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { QuoteStatusConfirmedLike } from '@pelicargo/utils';
import { useEffect, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { HiChevronDown } from 'react-icons/hi';
import { useLocation } from 'react-router-dom';

import { QuoteForm, useQuoteForm } from '../../hooks/quote/useForm';
import { Instruction, useInstructions } from '../../hooks/quote/useInstructions';
import { useUpdateQuote } from '../../hooks/quote/useUpdate';
import { Quote } from '../../utils/types';
import { Accordion, AccordionBody, AccordionHeader } from '../Accordion';
import { FeatureAllowed } from '../FeatureAllowed';
import { NewInstructionModal } from '../NewInstructionModal';
import { SimpleForm } from './../SimpleForm';
import { QuoteCardBody } from './QuoteCardBody';
import { QuoteCardHeader } from './QuoteCardHeader';

type Props = CardProps & {
  quote?: Quote;
  isToggleAll?: boolean;
  minCost?: number;
  isCheapest?: boolean;
  isSpotRate?: boolean;
  isPivotWeight?: boolean;
  isSelected?: boolean;
  originAddress?: Address;
  minTotalCost?: number;
  handleSelectQuote?: (quoteId: number) => void;
};

export const QuoteCard = ({
  quote,
  minCost,
  isCheapest,
  isSpotRate = false,
  isPivotWeight = false,
  isToggleAll,
  isSelected,
  originAddress,
  handleSelectQuote,
  minTotalCost,
  ...rest
}: Props) => {
  const [updateQuote, , error] = useUpdateQuote(quote?.id);

  const { methods } = useQuoteForm(quote);

  const { hash } = useLocation();

  useEffect(() => {
    if (error) methods.reset(quote);
  }, [error, methods, quote]);

  const isBookedQuote = QuoteStatusConfirmedLike.includes(quote?.status);
  const isAirQuote = quote?.freight_service === FreightService.AFR;

  const bgColor = useColorModeValue('white', 'gray.700');

  const isAtCurrentQuote = useMemo(
    () => quote?.quote_variants?.some((v) => hash.replace('#QV', '') === v.id.toString()),
    [quote?.quote_variants, hash],
  );
  const borderColor = isAtCurrentQuote ? 'primary.500' : null;

  return (
    <SimpleForm defaultValues={quote} onValid={updateQuote} {...(methods as UseFormReturn<QuoteForm>)}>
      <Flex
        direction="column"
        maxW="full"
        borderRadius={8}
        bg={bgColor || 'border.default'}
        borderWidth={1}
        borderColor={borderColor}
        _hover={{ borderColor: borderColor || 'border.emphasized' }}
        transition="border-color 100ms linear"
        {...rest}
        tabIndex={0}
        className="quote"
      >
        <Accordion isOpen={isToggleAll || isAtCurrentQuote}>
          <AccordionHeader>
            <QuoteCardHeader
              quote={quote}
              minCost={minCost}
              minTotalCost={minTotalCost}
              isCheapest={isCheapest}
              isSelected={isSelected}
              isSpotRate={isSpotRate}
              isPivotWeight={isPivotWeight}
              originAddress={originAddress}
              handleSelectQuote={handleSelectQuote}
              px="3"
            />
          </AccordionHeader>
          <AccordionBody>
            <QuoteCardBody quote={quote} isBookingOpen={isBookedQuote || isToggleAll || isAirQuote} />
          </AccordionBody>
        </Accordion>
      </Flex>
    </SimpleForm>
  );
};

type InstructionTemplateMenuProps = {
  quote: Quote;
  airportId: string;
};

const TAG_COLOUR_SCHEME = {
  Freighter: 'red',
  RFS: 'purple',
  WideBody: 'orange',
  NarrowBody: 'blue',
};

export const InstructionTemplateMenu = ({ quote, airportId }: InstructionTemplateMenuProps) => {
  const quoteId = quote?.id;
  const providerId = quote?.provider_id;

  const newInstructionModalDisclosure = useDisclosure();
  const { data, isLoading, isSetting, hasNone, handleSetInstructionsForQuote } = useInstructions({
    quoteId,
    providerId,
    airportId,
  });

  const handleInstructionSelect = (instructionId: number) => () => {
    handleSetInstructionsForQuote({ quoteId, instructionId });
  };

  const renderItem = ({ id, aircraft_type, provider_id, airport_id }: Instruction) => {
    const colorScheme = aircraft_type ? TAG_COLOUR_SCHEME?.[aircraft_type] : 'green';

    return (
      <MenuItem onClick={handleInstructionSelect(id)} key={id}>
        {provider_id} {airport_id}
        <Badge ml="2" colorScheme={colorScheme} key={aircraft_type}>
          {aircraft_type || 'Default'}
        </Badge>
      </MenuItem>
    );
  };

  return (
    <>
      <Menu>
        <MenuButton as={Button} size="sm" isLoading={isLoading || isSetting}>
          Templates <Icon as={HiChevronDown} ml="2" />
        </MenuButton>
        <MenuList>
          <MenuGroup title="Templates">{data?.map(renderItem)}</MenuGroup>
          <Show if={hasNone}>
            <Text fontSize="xxs" color="gray.500" textAlign="center">
              None Found!
            </Text>
          </Show>
          <FeatureAllowed feature="instructionCreation">
            <MenuDivider />
            <MenuItem onClick={newInstructionModalDisclosure.onOpen}>Add New Template</MenuItem>
          </FeatureAllowed>
        </MenuList>
      </Menu>
      <NewInstructionModal
        defaultValues={{ provider_id: providerId, airport_id: airportId }}
        {...newInstructionModalDisclosure}
      />
    </>
  );
};
